import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardCheck, CheckCircle, XCircle, AlertTriangle, Clock, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { parseDate } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import StatusBadge from '../components/time-off/StatusBadge';
import Modal from '../components/common/Modal';
import CountryFlag from '../components/common/CountryFlag';
import { useToast } from '../components/common/Toast';
import './Approvals.css';

function Approvals() {
  const { user } = useAuth();
  const toast = useToast();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [denyRequestId, setDenyRequestId] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState('');

  // Remove modal (retroactive removal of approved requests)
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeRequestId, setRemoveRequestId] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [approvedRequests, setApprovedRequests] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const deptParam = filterDept ? `&department_id=${filterDept}` : '';
      const [pendingRes, approvedRes, deptRes] = await Promise.allSettled([
        api.get(`/requests/pending${filterDept ? `?department_id=${filterDept}` : ''}`),
        api.get(`/requests?status=approved${deptParam}&limit=20`),
        api.get('/departments'),
      ]);
      setRequests(pendingRes.status === 'fulfilled' ? (pendingRes.value.requests || []) : []);
      setApprovedRequests(approvedRes.status === 'fulfilled' ? (approvedRes.value.requests || []) : []);
      setDepartments(deptRes.status === 'fulfilled' ? (deptRes.value.departments || []) : []);
    } catch (err) {
      // Don't loop -- just show empty state
    } finally {
      setLoading(false);
    }
  }, [filterDept]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      await api.post(`/requests/${requestId}/approve`);
      toast.success('Request approved');
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDenyClick = (requestId) => {
    setDenyRequestId(requestId);
    setDenyReason('');
    setDenyModalOpen(true);
  };

  const handleDenyConfirm = async () => {
    if (!denyReason.trim()) {
      toast.error('A denial reason is required');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/requests/${denyRequestId}/deny`, { reason: denyReason.trim() });
      toast.success('Request denied');
      setDenyModalOpen(false);
      setDenyRequestId(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to deny request');
    } finally {
      setProcessing(false);
    }
  };

  // Retroactive removal of approved requests
  const handleRemoveClick = (requestId) => {
    setRemoveRequestId(requestId);
    setRemoveReason('');
    setRemoveModalOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!removeReason.trim()) {
      toast.error('A removal reason is required');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/requests/${removeRequestId}/remove`, { reason: removeReason.trim() });
      toast.success('Request removed');
      setRemoveModalOpen(false);
      setRemoveRequestId(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove request');
    } finally {
      setProcessing(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    try {
      const res = await api.post('/requests/batch-approve', {
        request_ids: Array.from(selectedIds),
      });
      const results = res.results || [];
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) toast.success(`${successCount} request(s) approved`);
      if (failCount > 0) toast.warning(`${failCount} request(s) failed`);
    } catch (err) {
      toast.error(err.message || 'Batch approve failed');
    }

    setSelectedIds(new Set());
    setProcessing(false);
    loadData();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === requests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(requests.map(r => r.id)));
    }
  };

  return (
    <div className="approvals">
      <div className="page-header">
        <ClipboardCheck size={24} strokeWidth={2} />
        <h1>Approval Queue</h1>
        {selectedIds.size > 0 && (
          <button className="btn-primary" onClick={handleBatchApprove} disabled={processing}>
            <CheckCircle size={14} />
            Approve Selected ({selectedIds.size})
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="approvals-filters">
        <select
          className="form-select"
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
        >
          <option value="">All Departments</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <span className="approvals-count">
          {requests.length} pending request{requests.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="approvals-loading">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="approvals-empty">
          <CheckCircle size={32} strokeWidth={1.5} />
          <p>No pending requests to review</p>
        </div>
      ) : (
        <div className="approvals-list">
          <div className="approvals-table-header">
            <label className="approvals-checkbox-cell">
              <input
                type="checkbox"
                checked={selectedIds.size === requests.length}
                onChange={toggleSelectAll}
              />
            </label>
            <span>Employee</span>
            <span>Dates</span>
            <span>Days</span>
            <span>Flags</span>
            <span>Pending</span>
            <span>Starts In</span>
            <span>Actions</span>
          </div>

          {requests.map(request => {
            const days = request.days || [];
            const firstDate = days.length > 0 ? days[0].date : null;
            const lastDate = days.length > 0 ? days[days.length - 1].date : null;

            return (
              <div key={request.id} className="approvals-row">
                <label className="approvals-checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(request.id)}
                    onChange={() => toggleSelect(request.id)}
                  />
                </label>

                <div className="approvals-employee">
                  <span className="approvals-employee-name">
                    {request.first_name || request.user_name}
                  </span>
                  {request.country_code && (
                    <span className="approvals-employee-country-row">
                      <CountryFlag code={request.country_code} size="small" />
                      <span className="approvals-employee-country">{request.country_code}</span>
                    </span>
                  )}
                </div>

                <div className="approvals-dates">
                  {firstDate && (
                    <span>
                      {firstDate && format(parseDate(firstDate), 'MMM d')}
                      {lastDate && lastDate !== firstDate && (
                        <> - {format(parseDate(lastDate), 'MMM d')}</>
                      )}
                    </span>
                  )}
                </div>

                <span className="approvals-total">{request.total_days}</span>

                <div className="approvals-flags">
                  {request.is_short_notice && (
                    <span className="flag-badge flag-warning" title="Short notice">
                      <AlertTriangle size={10} /> Short
                    </span>
                  )}
                  {request.insufficient_balance && (
                    <span className="flag-badge flag-error" title="Insufficient balance">
                      <AlertTriangle size={10} /> Low
                    </span>
                  )}
                  {request.category === 'birthday' && (
                    <span className="flag-badge flag-info">Bday</span>
                  )}
                </div>

                <span className="approvals-age">
                  {request.pending_age_days}d
                </span>

                <span className="approvals-starts-in">
                  {request.days_until_start !== null ? (
                    request.days_until_start <= 0 ? (
                      <span className="text-warning">Today/Past</span>
                    ) : (
                      `${request.days_until_start}d`
                    )
                  ) : '-'}
                </span>

                <div className="approvals-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(request.id)}
                    disabled={processing}
                    title="Approve"
                  >
                    <CheckCircle size={16} />
                  </button>
                  <button
                    className="btn-deny"
                    onClick={() => handleDenyClick(request.id)}
                    disabled={processing}
                    title="Deny"
                  >
                    <XCircle size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recently Approved -- retroactive removal section */}
      {approvedRequests.length > 0 && (
        <>
          <div className="page-header" style={{ marginTop: 'var(--space-8)' }}>
            <CheckCircle size={20} strokeWidth={2} />
            <h2 style={{ fontSize: 'var(--font-size-lg)' }}>Recently Approved</h2>
          </div>
          <div className="approvals-list">
            <div className="approvals-table-header">
              <span>Employee</span>
              <span>Dates</span>
              <span>Days</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            {approvedRequests.map(request => {
              const days = request.days || [];
              const firstDate = days.length > 0 ? days[0].date : null;
              const lastDate = days.length > 0 ? days[days.length - 1].date : null;

              return (
                <div key={request.id} className="approvals-row">
                  <div className="approvals-employee">
                    <span className="approvals-employee-name">
                      {request.first_name || request.user_name}
                    </span>
                    {request.country_code && (
                      <span className="approvals-employee-country-row">
                        <CountryFlag code={request.country_code} size="small" />
                        <span className="approvals-employee-country">{request.country_code}</span>
                      </span>
                    )}
                  </div>
                  <div className="approvals-dates">
                    {firstDate && (
                      <span>
                        {format(parseDate(firstDate), 'MMM d')}
                        {lastDate && lastDate !== firstDate && (
                          <> - {format(parseDate(lastDate), 'MMM d')}</>
                        )}
                      </span>
                    )}
                  </div>
                  <span className="approvals-total">{request.total_days}</span>
                  <StatusBadge status={request.status} />
                  <div className="approvals-actions">
                    <button
                      className="btn-deny"
                      onClick={() => handleRemoveClick(request.id)}
                      disabled={processing}
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Deny Reason Modal */}
      <Modal
        isOpen={denyModalOpen}
        onClose={() => setDenyModalOpen(false)}
        title="Deny Request"
        size="small"
      >
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label className="form-label form-label-required" style={{ marginBottom: '8px', display: 'block' }}>
            Reason for denial
          </label>
          <textarea
            className="form-textarea"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Please provide a reason for denying this request..."
            rows={3}
            style={{ width: '100%' }}
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setDenyModalOpen(false)} disabled={processing}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDenyConfirm} disabled={processing || !denyReason.trim()}>
            {processing ? 'Denying...' : 'Deny Request'}
          </button>
        </div>
      </Modal>

      {/* Remove Reason Modal (retroactive removal) */}
      <Modal
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        title="Remove Approved Request"
        size="small"
      >
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            This will retroactively remove an approved time off request and restore the employee's PTO balance.
          </p>
          <label className="form-label form-label-required" style={{ marginBottom: '8px', display: 'block' }}>
            Reason for removal
          </label>
          <textarea
            className="form-textarea"
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            placeholder="Please explain why this approved request is being removed..."
            rows={3}
            style={{ width: '100%' }}
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => setRemoveModalOpen(false)} disabled={processing}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleRemoveConfirm} disabled={processing || !removeReason.trim()}>
            {processing ? 'Removing...' : 'Remove Request'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Approvals;
